import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { UserContext } from "../../App";
import axios from "axios";
import { apiRoot } from "../../index";
import { validateEstimateInvoiceBooking } from "./index";
import { useHistory } from "react-router-dom";
import { BookingForm } from "./BookingForm";
import Footer from "./Footer";
import { useProviderOptions } from "../../store/actions/providerActions";
import Select from "react-select";

const defaultValue = {
  passengersNumber: 0,
  customer: null,
  bagsNumber: 0,
  vehicle: null,
  tarif: null,
  pickupDate: "",
  pickupTime: "",
  pickupAddressInput: "",
  pickupAddress: "",
  dropoffAddressInput: "",
  dropoffAddress: "",
  flightTrainNumber: "",
  additionalInfo: "",
  tripType: null,
  numHours: 0,
  price: 0,
  tva: 10,
};

export default function CreateOrUpdateContentForm({
  rawObject,
  tarifs,
  isEstimate,
  loading,
  setLoading,
}) {
  const history = useHistory();
  const { userInfos } = useContext(UserContext);

  const _providerOptions = useProviderOptions(false);
  const providerOptions = _providerOptions?.map((option) => {
    return {
      label: option.label,
      value: option.value,
      data: {
        id: option.data.id,
        firstname: option.data.name,
        lastname: "",
        email: option.data.email,
        phone: option.data.tel,
        provider: option.data,
        isProvider: true,
      },
    };
  });
  const [provider, setProvider] = useState(null);
  const [avoidBookingCreation, setAvoidBookingCreation] = useState(false);
  const [isOpenField, setIsOpenField] = useState(false);
  const [text, setText] = useState("");
  const [price, setPrice] = useState(0);
  const [tva, setTva] = useState(10);
  const [daysUntilDue, setDaysUntilDue] = useState(
    userInfos?.billing?.defaultDaysUntilDue || 30
  );
  const [bookings, setBookings] = useState([{ ...defaultValue }]);

  const readOnly =
    rawObject &&
    rawObject.id &&
    (rawObject.status !== "draft" || rawObject?.sharedBy);
  const cannotDelete =
    rawObject && rawObject.id && rawObject.status !== "draft";

  // region init & methods

  // Init objects on init (when rawObject is set)
  useEffect(() => {
    if (!rawObject) return;

    setBookings(rawObject.bookings);
    setAvoidBookingCreation(rawObject.avoidBookingCreation);
    setIsOpenField(rawObject.isOpenField || false);
    setText(rawObject.openField?.text || "");
    setPrice(rawObject.openField?.price || 0);
    if (!isNaN(parseFloat(rawObject.openField?.tva))) {
      setTva(rawObject.openField?.tva);
    } else {
      setTva(10);
    }
    setDaysUntilDue(
      rawObject.daysUntilDue || userInfos?.billing?.defaultDaysUntilDue || 30
    );

    if (rawObject?.customer) {
      const customerLabel =
        (rawObject.customer.isProvider ? "[Client] " : "") +
        rawObject.customer.firstname +
        " " +
        rawObject.customer.lastname;
      setProvider({
        label: customerLabel,
        data: rawObject.customer,
        value: rawObject.customer.key,
      });
    }
  }, [
    rawObject,
    rawObject?.bookings,
    rawObject?.customer,
    userInfos?.billing?.defaultDaysUntilDue,
  ]);

  // field setter
  const getSetter = useCallback((index) => {
    return (mod) => {
      setBookings((old) => {
        let newVal;
        if (typeof mod === "function") {
          newVal = mod(old[index]);
        } else {
          newVal = mod;
        }

        let newArr = old.slice();
        newArr[index] = newVal;

        return newArr;
      });
    };
  }, []);

  function createObject() {
    try {
      if (!provider) {
        throw new Error("Veuillez sélectionner un client");
      }
      if (!provider.data.email) {
        throw new Error("L'adresse mail du client n'est pas renseignée");
      }

      // If isOpenField is true, validate the required fields
      if (isOpenField) {
        if (price <= 0) {
          throw new Error("Le prix doit être supérieur à 0");
        }
      }

      let _bookings = [];

      // Only process bookings if isOpenField is false
      if (!isOpenField) {
        _bookings = bookings.map((booking) => {
          let bookingCustomer = booking.customer ? booking.customer.data : null;

          const bookingData = {
            ...booking,
            tripType: booking.tripType ? booking.tripType.value : null,
            vehicle: booking.vehicle ? booking.vehicle.value : null,
            tarif: booking.tarif ? booking.tarif.value : null,
            tarif_product: booking.tarif_product
              ? booking.tarif_product.value
              : null,
            customer: bookingCustomer,
            price: parseFloat(booking.price),
          };

          // throws exception on error
          validateEstimateInvoiceBooking(bookingData);

          return bookingData;
        });
      }

      setLoading(true);

      let apiRoute = "";
      let body = {};
      if (isEstimate) {
        apiRoute = "/api/v1/dispatch/create-estimate";
        body = {
          estimate: {
            customer: provider.data,
            bookings: _bookings,
            id: rawObject?.id,
            avoidBookingCreation: avoidBookingCreation,
            isOpenField: isOpenField,
            daysUntilDue: daysUntilDue,
            openField: isOpenField
              ? {
                  text: text,
                  price: price,
                  tva: tva,
                }
              : null,
          },
        };
      } else {
        apiRoute = "/api/v1/dispatch/create-invoice";
        body = {
          invoice: {
            customer: provider.data,
            bookings: _bookings,
            id: rawObject?.id,
            avoidBookingCreation: avoidBookingCreation,
            isOpenField: isOpenField,
            daysUntilDue: daysUntilDue,
            openField: isOpenField
              ? {
                  text: text,
                  price: price,
                  tva: tva,
                }
              : null,
          },
        };
      }

      axios
        .post(apiRoot + apiRoute, body, {
          headers: {
            Authorization: userInfos.jwt,
          },
        })
        .then((res) => {
          setLoading(false);
          if (!rawObject?.id) {
            if (isEstimate) history.push("/estimates");
            else history.push("/invoices");
          }
        })
        .catch((e) => {
          if (e?.response?.data === "Invalid value for eu_vat.") {
            window.alert("Le numéro de TVA du client est invalide.");
            setLoading(false);
          } else {
            window.alert(e.response.data);
          }

          setLoading(false);
        });
    } catch (e) {
      window.alert(e.message);
    }
  }

  // Remove customer on bookings if customer is not from selected provider
  const providerUpdated = useCallback(
    (newProviderId) => {
      for (let i = 0; i < bookings.length; i++) {
        let booking = bookings[i];
        if (booking.customer) {
          let customer = booking.customer.data;
          if (customer?.provider?.id !== newProviderId) {
            let setter = getSetter(i);
            setter((old) => {
              return {
                ...old,
                customer: null,
              };
            });
          }
        }
      }
    },
    [bookings, getSetter]
  );

  // endregion init
  let submitBtnText = useMemo(() => {
    const objectLabel = isEstimate ? "Devis" : "Facture";
    let res = rawObject?.id
      ? "Mettre à jour " + objectLabel
      : "Créer " + objectLabel;
    if (loading) res = rawObject?.id ? "Mise à jour..." : "Création...";

    return res;
  }, [isEstimate, rawObject?.id, loading]);

  return (
    <form onSubmit={(e) => e.preventDefault()} className="kt-form">
      <div className="kt-portlet__body">
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">Client *</label>
          <div className="col-lg-6">
            <Select
              value={provider}
              isDisabled={readOnly}
              options={providerOptions}
              onChange={(val) => {
                setProvider(val);
                providerUpdated(val.value);
              }}
            />
          </div>
        </div>
        {provider?.data?.email && (
          <div className="form-group row">
            <label className="col-lg-3 col-form-label">Email</label>
            <div className="col-lg-6">
              <input
                type="email"
                className="form-control"
                value={provider.data.email}
                disabled={true}
                style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
              />
            </div>
          </div>
        )}
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">Champ libre</label>
          <div className="col-lg-6">
            <div className="form-check form-check-inline">
              <input
                type="radio"
                className="form-check-input"
                id="champLibreOui"
                name="champLibre"
                checked={isOpenField}
                onChange={() => setIsOpenField(true)}
                disabled={readOnly}
              />
              <label className="form-check-label" htmlFor="champLibreOui">
                Oui
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                type="radio"
                className="form-check-input"
                id="champLibreNon"
                name="champLibre"
                checked={!isOpenField}
                onChange={() => setIsOpenField(false)}
                disabled={readOnly}
              />
              <label className="form-check-label" htmlFor="champLibreNon">
                Non
              </label>
            </div>
          </div>
        </div>
        {!isOpenField ? (
          <>
            {bookings.map((booking, index) => (
              <BookingForm
                key={index}
                userInfos={userInfos}
                tarifs={tarifs}
                data={booking}
                setData={getSetter(index)}
                index={index}
                provider={provider?.data}
                isEstimate={isEstimate}
                updateProvider={(providerId) => {
                  let newProvider = providerOptions.find(
                    (_p) => _p.value === providerId
                  );
                  setProvider(newProvider);
                }}
                bookingId={
                  rawObject &&
                  rawObject.bookings &&
                  rawObject.bookings[index] &&
                  rawObject.bookings[index].bookingId
                    ? rawObject.bookings[index].bookingId
                    : null
                }
                deleteBooking={() => {
                  setBookings((old) => {
                    let newArr = old.slice();
                    newArr.splice(index, 1);
                    return newArr;
                  });
                }}
                duplicateBooking={() => {
                  setBookings((old) => {
                    return [...old, { ...old[index] }];
                  });
                }}
                disabled={readOnly}
              />
            ))}
            {!readOnly && (
              <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                <div
                  style={{ width: "36px", height: "36px" }}
                  className="ratio ratio-1x1"
                >
                  <button
                    className="btn btn-primary rounded-circle ration ratio-1x1 p-0 d-flex align-items-center justify-content-center"
                    onClick={() => {
                      setBookings((old) => [...old, { ...defaultValue }]);
                    }}
                  >
                    <i
                      className="fas fa-plus p-0"
                      style={{ width: "16px" }}
                    ></i>
                  </button>
                </div>
                <p>Ajouter une course</p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="form-group row">
              <label className="col-lg-3 col-form-label">Texte :</label>
              <div className="col-lg-6">
                <textarea
                  className="form-control"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="form-group row">
              <label className="col-lg-3 col-form-label">Prix :</label>
              <div className="col-lg-6">
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={price}
                  onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="form-group row">
              <label className="col-lg-3 col-form-label">TVA :</label>
              <div className="col-lg-6">
                <input
                  type="number"
                  step="1"
                  className="form-control"
                  value={tva}
                  onChange={(e) => setTva(parseFloat(e.target.value) || 0)}
                  disabled={readOnly}
                />
              </div>
            </div>
          </>
        )}
        {!isOpenField && (
          <div className="form-group row align-items-center">
            <label className="col-lg-3 col-form-label">
              Création automatique de bookings :
            </label>
            <div className="col-lg-6">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={!avoidBookingCreation}
                  name="avoidBookingCreation"
                  onChange={(e) => {
                    let checked = e.target.checked;
                    setAvoidBookingCreation(!checked);
                  }}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>
        )}
        <div className="form-group row">
          <label className="col-lg-3 col-form-label">
            {isEstimate
              ? "Jours avant échéance (pour la facture générée) :"
              : "Jours avant échéance :"}
          </label>
          <div className="col-lg-6">
            <input
              type="number"
              step="1"
              min="1"
              className="form-control"
              value={daysUntilDue}
              onChange={(e) => setDaysUntilDue(parseInt(e.target.value) || 30)}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>
      <Footer
        readOnly={readOnly}
        cannotDelete={cannotDelete}
        isEstimate={isEstimate}
        rawObject={rawObject}
        loading={loading}
        setLoading={setLoading}
        createButton={
          <button
            type="submit"
            className="btn btn-primary px-5"
            onClick={createObject}
            disabled={loading}
          >
            {submitBtnText}
          </button>
        }
      />
    </form>
  );
}
